<div class="order-detail-page">
  <div class="page-header">
    <button nz-button nzType="text" (click)="navigateBack.emit()" class="back-link">
      <i nz-icon nzType="arrow-left"></i>
      {{ 'SIGN_ORDER.DETAIL.BACK_TO_LIST' | translate }}
    </button>
  </div>

  <nz-alert *ngIf="errorMessage" nzType="error" [nzMessage]="errorMessage" nzShowIcon></nz-alert>

  <nz-spin [nzSpinning]="isLoading">
    <ng-container *ngIf="order">
      <div class="detail-hero">
        <div class="detail-hero-left">
          <h1>{{ 'SIGN_ORDER.DETAIL.TITLE' | translate:{orderNumber: order.orderNumber} }}</h1>
          <span class="detail-date">{{ formatSignOrderDateTime(order.createdAt, translate.currentLang) }}</span>
        </div>
        <nz-tag [nzColor]="getStatusColor(order.status)" class="detail-status-tag">
          {{ getStatusLabel(order.status) }}
        </nz-tag>
      </div>

      <div class="detail-grid">
        <div class="detail-section">
          <div class="detail-section-title">
            <i nz-icon nzType="info-circle"></i>
            {{ 'SIGN_ORDER.DETAIL.ORDER_INFO' | translate }}
          </div>
          <div class="detail-section-body">
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.PRODUCT' | translate }}</span>
              <span class="detail-val">{{ localizedText(order.product?.name) }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.UNIQUE_PROPERTIES' | translate }}</span>
              <span class="detail-val">{{ order.uniquePropertyCount }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.TOTAL_COPIES' | translate }}</span>
              <span class="detail-val">{{ order.totalCopies }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.DRILLING_OPTION' | translate }}</span>
              <span
                class="detail-val">{{ 'SIGN_ORDER.CHECKOUT.DRILLING_' + order.drillingOption?.toUpperCase() | translate }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.CHECKOUT.TOTAL_WEIGHT' | translate }}</span>
              <span class="detail-val">{{ formatSignOrderWeight(order.pricing?.totalWeightGrams, translate.currentLang) }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.PAYMENT_REFERENCE' | translate }}</span>
              <span class="detail-val"><code>{{ order.paymentReference }}</code></span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-title">
            <i nz-icon nzType="bank"></i>
            {{ 'SIGN_ORDER.DETAIL.BILLING_ADDRESS' | translate }}
          </div>
          <div class="detail-section-body address-detail" *ngIf="order.billingAddressSameAsShipping; else separateBillingAddress">
            <p>{{ 'SIGN_ORDER.DETAIL.BILLING_SAME_AS_SHIPPING' | translate }}</p>
            <p>{{ order.billingAddress?.companyName }}</p>
            <p>{{ order.billingAddress?.contactName }}</p>
            <p>{{ order.billingAddress?.street }} {{ order.billingAddress?.houseNumber }}</p>
            <p *ngIf="order.billingAddress?.addressLine2">{{ order.billingAddress?.addressLine2 }}</p>
            <p>{{ order.billingAddress?.postalCode }} {{ order.billingAddress?.city }}</p>
            <p>{{ translate.currentLang === 'de' ? 'Deutschland' : 'Germany' }}</p>
          </div>
          <ng-template #separateBillingAddress>
            <div class="detail-section-body address-detail">
              <p>{{ order.billingAddress?.companyName }}</p>
              <p>{{ order.billingAddress?.contactName }}</p>
              <p>{{ order.billingAddress?.street }} {{ order.billingAddress?.houseNumber }}</p>
              <p *ngIf="order.billingAddress?.addressLine2">{{ order.billingAddress?.addressLine2 }}</p>
              <p>{{ order.billingAddress?.postalCode }} {{ order.billingAddress?.city }}</p>
              <p>{{ translate.currentLang === 'de' ? 'Deutschland' : 'Germany' }}</p>
            </div>
          </ng-template>
        </div>

        <div class="detail-section" *ngIf="order.pricing">
          <div class="detail-section-title">
            <i nz-icon nzType="dollar"></i>
            {{ 'SIGN_ORDER.DETAIL.PRICING' | translate }}
          </div>
          <div class="detail-section-body">
            <div class="detail-row">
              <span>{{ (order.drillingOption === 'with_drilling' ? 'SIGN_ORDER.CHECKOUT.UNIT_PRICE_INCL_DRILLING' : 'SIGN_ORDER.CHECKOUT.UNIT_PRICE') | translate }}</span>
              <span class="detail-val">{{ formatCents(order.pricing.combinedUnitNetCents, order.currency) }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.CHECKOUT.SIGNS_SUBTOTAL' | translate }}</span>
              <span class="detail-val">{{ formatCents(order.pricing.signsNetSubtotalCents, order.currency) }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.CHECKOUT.SHIPPING' | translate }}</span>
              <span class="detail-val">{{ formatCents(order.pricing.shippingNetCents, order.currency) }}</span>
            </div>
            <div class="detail-row detail-row-divider"></div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.CHECKOUT.TAXABLE_NET' | translate }}</span>
              <span class="detail-val">{{ formatCents(order.pricing.taxableNetCents, order.currency) }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.CHECKOUT.VAT' | translate }} ({{ formatVatRate(order.pricing.vatRateBasisPoints) }}
                )</span>
              <span class="detail-val">{{ formatCents(order.pricing.vatAmountCents, order.currency) }}</span>
            </div>
            <div class="detail-row detail-row-total">
              <span>{{ 'SIGN_ORDER.CHECKOUT.GROSS_TOTAL' | translate }}</span>
              <span class="detail-val">{{ formatCents(order.pricing.grossTotalCents, order.currency) }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-title">
            <i nz-icon nzType="home"></i>
            {{ 'SIGN_ORDER.DETAIL.PROPERTIES' | translate }}
          </div>
          <div class="detail-section-body no-padding">
            <nz-table #customerOrderItemsTable [nzData]="orderItems" nzSize="small" [nzBordered]="false"
                      [nzPageSize]="20">
              <thead>
              <tr>
                <th>{{ 'SIGN_ORDER.DETAIL.PROPERTY_ADDRESS' | translate }}</th>
                <th style="text-align:right">{{ 'SIGN_ORDER.DETAIL.COPIES' | translate }}</th>
              </tr>
              </thead>
              <tbody>
              <tr *ngFor="let item of customerOrderItemsTable.data">
                <td>{{ item.address }}</td>
                <td style="text-align:right"><strong>{{ item.copies }}</strong></td>
              </tr>
              </tbody>
            </nz-table>
          </div>
        </div>

        <div class="detail-section" *ngIf="order.userVisibleNote">
          <div class="detail-section-title">
            <i nz-icon nzType="message"></i>
            {{ 'SIGN_ORDER.DETAIL.NOTE' | translate }}
          </div>
          <div class="detail-section-body">
            <p>{{ order.userVisibleNote }}</p>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-title">
            <i nz-icon nzType="environment"></i>
            {{ 'SIGN_ORDER.DETAIL.SHIPPING_ADDRESS' | translate }}
          </div>
          <div class="detail-section-body address-detail">
            <p>{{ order.shippingAddress?.companyName }}</p>
            <p>{{ order.shippingAddress?.contactName }}</p>
            <p>{{ order.shippingAddress?.street }} {{ order.shippingAddress?.houseNumber }}</p>
            <p *ngIf="order.shippingAddress?.addressLine2">{{ order.shippingAddress.addressLine2 }}</p>
            <p>{{ order.shippingAddress?.postalCode }} {{ order.shippingAddress?.city }}</p>
            <p>{{ translate.currentLang === 'de' ? 'Deutschland' : 'Germany' }}</p>
            <p class="address-email">{{ order.shippingAddress?.email }}</p>
            <p *ngIf="order.shippingAddress?.phone">{{ order.shippingAddress.phone }}</p>
          </div>
        </div>

        <div class="detail-section" *ngIf="order.shipment?.carrier || order.shipment?.trackingNumber || order.shipment?.trackingUrl || order.shipment?.note?.trim()">
          <div class="detail-section-title">
            <i nz-icon nzType="car"></i>
            {{ 'SIGN_ORDER.DETAIL.SHIPMENT_INFO' | translate }}
          </div>
          <div class="detail-section-body">
            <div class="detail-row" *ngIf="order.shippedAt">
              <span>{{ 'SIGN_ORDER.DETAIL.SHIPPED_AT' | translate }}</span>
              <span class="detail-val">{{ formatSignOrderDateTime(order.shippedAt, translate.currentLang) }}</span>
            </div>
            <div class="detail-row" *ngIf="order.shipment?.carrier">
              <span>{{ 'SIGN_ORDER.DETAIL.CARRIER' | translate }}</span>
              <span class="detail-val">{{ order.shipment?.carrier }}</span>
            </div>
            <div class="detail-row" *ngIf="order.shipment?.trackingNumber">
              <span>{{ 'SIGN_ORDER.DETAIL.TRACKING_NUMBER' | translate }}</span>
              <span class="detail-val">{{ order.shipment?.trackingNumber }}</span>
            </div>
            <div class="detail-row" *ngIf="order.shipment?.trackingUrl">
              <span>{{ 'SIGN_ORDER.DETAIL.TRACKING_URL' | translate }}</span>
              <span class="detail-val"><a [href]="order.shipment?.trackingUrl" target="_blank" rel="noopener noreferrer">{{ order.shipment?.trackingUrl }}</a></span>
            </div>
            <div class="detail-row" *ngIf="order.shipment?.note?.trim()">
              <span>{{ 'SIGN_ORDER.DETAIL.SHIPMENT_NOTE' | translate }}</span>
              <span class="detail-val shipment-note-value">{{ order.shipment?.note }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" *ngIf="order.paymentInstructions">
          <div class="detail-section-title">
            <i nz-icon nzType="credit-card"></i>
            {{ 'SIGN_ORDER.DETAIL.PAYMENT_INFO' | translate }}
          </div>
          <div class="detail-section-body">
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.RECIPIENT' | translate }}</span>
              <span class="detail-val">{{ order.paymentInstructions.recipient }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.IBAN' | translate }}</span>
              <span class="detail-val"><code>{{ order.paymentInstructions.iban }}</code></span>
            </div>
            <div class="detail-row" *ngIf="order.paymentInstructions.bic">
              <span>{{ 'SIGN_ORDER.DETAIL.BIC' | translate }}</span>
              <span class="detail-val">{{ order.paymentInstructions.bic }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.PAYMENT_DUE' | translate }}</span>
              <span
                class="detail-val">{{ order.paymentInstructions.paymentDueDays }} {{ 'SIGN_ORDER.DETAIL.DAYS' | translate }}</span>
            </div>
            <div class="detail-row">
              <span>{{ 'SIGN_ORDER.DETAIL.REFERENCE' | translate }}</span>
              <span class="detail-val"><code>{{ order.paymentReference }}</code></span>
            </div>
            <div class="detail-row" *ngIf="order.invoiceNumber?.trim()">
              <span>{{ 'SIGN_ORDER.DETAIL.INVOICE_NUMBER' | translate }}</span>
              <span class="detail-val"><code>{{ order.invoiceNumber }}</code></span>
            </div>
            <div class="detail-row detail-row-divider"></div>
            <p class="payment-text">{{ localizedText(order.paymentInstructions.instructions) }}</p>
          </div>
        </div>

        <div class="detail-section" *ngIf="order.legalAcceptanceSnapshot">
          <div class="detail-section-title">
            <i nz-icon nzType="safety"></i>
            {{ 'SIGN_ORDER.DETAIL.LEGAL_ACCEPTANCE' | translate }}
          </div>
          <div class="detail-section-body">
            <p
              style="font-size:13px;color:#6b7280;margin-bottom:4px">{{ 'SIGN_ORDER.DETAIL.LEGAL_SNAPSHOT_NOTICE' | translate }}</p>
            <p style="font-size:12px;color:#9ca3af;margin-bottom:8px">{{ 'SIGN_ORDER.DETAIL.ACCEPTED_AT' | translate }}
              : {{ formatSignOrderDateTime(order.legalAcceptanceSnapshot.acceptedAt, translate.currentLang) }}</p>
            <a *ngIf="order.legalAcceptanceSnapshot.signOrderTermsUrl"
               [href]="order.legalAcceptanceSnapshot.signOrderTermsUrl" target="_blank" rel="noopener noreferrer"
               style="display:block;margin:4px 0;font-size:13px">{{ 'SIGN_ORDER.LEGAL.LINK_TERMS' | translate }}</a>
            <a *ngIf="order.legalAcceptanceSnapshot.generalTermsUrl"
               [href]="order.legalAcceptanceSnapshot.generalTermsUrl" target="_blank" rel="noopener noreferrer"
               style="display:block;margin:4px 0;font-size:13px">{{ 'SIGN_ORDER.LEGAL.LINK_GENERAL_TERMS' | translate }}</a>
            <a *ngIf="order.legalAcceptanceSnapshot.privacyNoticeUrl"
               [href]="order.legalAcceptanceSnapshot.privacyNoticeUrl" target="_blank" rel="noopener noreferrer"
               style="display:block;margin:4px 0;font-size:13px">{{ 'SIGN_ORDER.LEGAL.LINK_PRIVACY' | translate }}</a>
            <a *ngIf="order.legalAcceptanceSnapshot.legalNoticeUrl"
               [href]="order.legalAcceptanceSnapshot.legalNoticeUrl" target="_blank" rel="noopener noreferrer"
               style="display:block;margin:4px 0;font-size:13px">{{ 'SIGN_ORDER.LEGAL.LINK_LEGAL_NOTICE' | translate }}</a>
          </div>
        </div>

        <div class="detail-section" *ngIf="getStatusHistory().length">
          <div class="detail-section-title">
            <i nz-icon nzType="clock-circle"></i>
            {{ 'SIGN_ORDER.DETAIL.STATUS_HISTORY' | translate }}
          </div>
          <div class="detail-section-body">
            <nz-timeline>
              <nz-timeline-item *ngFor="let entry of getStatusHistory()" [nzColor]="getStatusColor(entry.nextStatus)">
                <p class="timeline-title">{{ getStatusLabel(entry.nextStatus) }}</p>
                <p class="timeline-date">{{ formatSignOrderDateTime(entry.changedAt, translate.currentLang) }}</p>
                <p class="timeline-reason" *ngIf="entry.reason">{{ entry.reason }}</p>
              </nz-timeline-item>
            </nz-timeline>
          </div>
        </div>
      </div>
    </ng-container>
  </nz-spin>
</div>
